/**
 * runtime.js — runtime-writable property metadata (Schema V2).
 *
 * A property declares itself changeable at runtime with
 *
 *     runtime: { writable: true }
 *
 * The metadata is transport-agnostic on purpose: the Home Assistant plugin
 * is the first consumer, but MQTT and the native API need exactly the same
 * answer to "may a device-side transport write this property?". Nothing here
 * is HASS-specific.
 *
 * Rules:
 *  - Missing `runtime.writable` means NOT writable. There is no implicit
 *    "everything non-structural is writable" fallback — Designer-owned
 *    structure (a connection's from/to/points/route) simply stays unmarked.
 *  - `runtime.wireName` records the on-the-wire property name when it differs
 *    from the Schema V2 property name (a connection's `value` travels as
 *    `val`). It documents an existing wire format; it is not an alias
 *    introduced for convenience.
 *
 * Schemas that declare no runtime metadata at all are treated as
 * "not yet migrated" by consumers, which keeps this an opt-in per object
 * type instead of a breaking change for every widget at once.
 */

import { toWireKey } from './wireFormat.js'

/** @private — [schemaName, definition, wireName] for every runtime-writable property. */
function* writableEntries(schema) {
    for (const [propName, def] of Object.entries(schema?.properties ?? {})) {
        if (def?.runtime?.writable === true) yield [propName, def, def.runtime.wireName ?? propName]
    }
    for (const [partName, partDef] of Object.entries(schema?.parts ?? {})) {
        for (const [propName, def] of Object.entries(partDef?.properties ?? {})) {
            if (def?.runtime?.writable === true) {
                yield [propName, def, def.runtime.wireName ?? toWireKey(partName, propName, partDef)]
            }
        }
    }
}

/**
 * Wire names of every property a runtime transport may write, in schema order.
 * @param {object|null|undefined} schema — a Schema V2 object schema
 * @returns {string[]}
 */
export function getRuntimeWritableProperties(schema) {
    return [...new Set([...writableEntries(schema)].map(([, , wireName]) => wireName))]
}

/** True when this schema has adopted runtime metadata at all. */
export function hasRuntimeWritableMetadata(schema) {
    return getRuntimeWritableProperties(schema).length > 0
}

/**
 * Which property names a runtime transport may bind, in wire form.
 *
 * This is the migration boundary, and it is exclusive on purpose:
 *
 *  - a schema that declares runtime metadata is bound *only* by
 *    `runtime.writable`; its `bindings.properties` block then carries
 *    strategy/metadata overrides and cannot widen the writable set;
 *  - a schema that has not adopted runtime metadata keeps the legacy
 *    behaviour, where `bindings.properties` is the list.
 *
 * Legacy entries are either a bare top-level property name (→ {}) or a part
 * name (→ { propName: {} }); the latter flattens to its firmware wire key,
 * because every consumer of this list needs real wire names.
 *
 * @param {object|null|undefined} schema
 * @returns {string[]}
 */
export function getBindableProperties(schema) {
    if (hasRuntimeWritableMetadata(schema)) return getRuntimeWritableProperties(schema)

    const names = []
    for (const [key, value] of Object.entries(schema?.bindings?.properties ?? {})) {
        if (schema?.parts?.[key]) {
            for (const propName of Object.keys(value ?? {})) {
                names.push(toWireKey(key, propName, schema.parts[key]))
            }
        } else {
            names.push(key)
        }
    }
    return [...new Set(names)]
}

/**
 * Resolve an authored property name to the wire name a runtime transport uses.
 * Returns null when the property is not runtime-writable for this schema —
 * callers use that to reject bindings on Designer-owned properties.
 *
 * Both the Schema V2 name and the wire name resolve (a design authored before
 * the rename stores the wire name directly).
 *
 * @param {object|null|undefined} schema
 * @param {string} property
 * @returns {string|null}
 */
export function resolveRuntimeWireName(schema, property) {
    for (const [propName, , wireName] of writableEntries(schema)) {
        if (property === propName || property === wireName) return wireName
    }
    return null
}
